<template>
  <div
    :class="[
      {
        'margin-allocation': true,
        'margin-allocation--secondary': !isDayTradeStep
      }
    ]"
  >
    <NavBar :class="navbarClass">
      <template v-if="!hasError" #left>
        <BackButton v-if="isDayTradeStep" icon="EF0030" @click="handleBack" />
        <BackButton v-else @click="currentStep = currentStep - 1" />
      </template>
      <template #right>
        <ShowMoneyToggleButton v-if="isDayTradeStep" />
        <BackButton v-else icon="EF0030" @click="handleBack" />
      </template>
    </NavBar>

    <FadeTransition>
      <LoaderIndicator
        v-if="isFetchingInitialData"
        class="margin-allocation__loader"
        with-container
      />
      <component :is="component" v-else />
    </FadeTransition>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, provide, ref } from 'vue';
import { useRouter } from 'vue-router/composables';
import { isBFFError } from '@/modules/common/helpers/bff';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';

import { NavBar, LoaderIndicator } from '@warrenbrasil/nebraska-web';
import { FadeTransition } from '@/modules/common/components/FadeTransition';
import {
  BackButton,
  ShowMoneyToggleButton
} from '@/modules/common/components/buttons';

import { MarginAllocationComponents, EMarginAllocationSteps } from './helpers';

import {
  getMarginAllocationV2,
  getMarginAllocationPreviewV2,
  updateLimitsV2,
  updatePreferences,
  IMarginAllocationV2,
  IMarginAllocationPreviewV2
} from '@/modules/trade/services/margin-allocation';

const router = useRouter();

const currentStep = ref(EMarginAllocationSteps.DAY_TRADE);
const marginAllocation = ref<IMarginAllocationV2 | null>(null);
const marginAllocationPreview = ref<IMarginAllocationPreviewV2 | null>(null);
const isFetchingInitialData = ref(false);
const hasError = ref(false);

const dataForm = ref({
  isUpdatingTraderShield: false,
  isUpdatingPreferences: false,
  isUpdatingLimits: false,
  balance: { value: 0, formatted: '' },
  drawdown: { value: 0, formatted: '' },
  isPreferencesSaved: false
});

const component = computed(() => MarginAllocationComponents[currentStep.value]);

const isDayTradeStep = computed(
  () => currentStep.value === EMarginAllocationSteps.DAY_TRADE
);

const navbarClass = computed(() => {
  return {
    'navbar-container': true,
    'navbar-container--secondary': !isDayTradeStep.value
  };
});

function handleBack() {
  router.push({ name: 'trade' });
}

function changeStep(step: EMarginAllocationSteps) {
  currentStep.value = step;
}

function updateDataForm(
  key: string,
  value: object | string | boolean | number
) {
  dataForm.value = {
    ...dataForm.value,
    [key]: value
  };
}

function updateBalanceDrawdown() {
  dataForm.value.balance = marginAllocation.value!.balance;
  dataForm.value.drawdown = marginAllocation.value!.dailyLoss;
  dataForm.value.isPreferencesSaved =
    marginAllocation.value!.isPreferencesSaved;
}

async function fetchMarginAllocation() {
  marginAllocation.value = await getMarginAllocationV2();
  updateBalanceDrawdown();
}

async function fetchInitialData() {
  try {
    hasError.value = false;
    isFetchingInitialData.value = true;

    const [marginDetails, preview] = await Promise.all([
      getMarginAllocationV2(),
      getMarginAllocationPreviewV2()
    ]);

    marginAllocation.value = marginDetails;
    marginAllocationPreview.value = preview;

    updateBalanceDrawdown();

    currentStep.value = EMarginAllocationSteps.DAY_TRADE;
  } catch {
    hasError.value = true;
    currentStep.value = EMarginAllocationSteps.ERROR;
  } finally {
    isFetchingInitialData.value = false;
  }
}

async function fetchUpdateLimits(isDeallocate = false) {
  try {
    dataForm.value.isUpdatingLimits = true;

    marginAllocation.value = await updateLimitsV2({
      marginCapital: dataForm.value.balance.value,
      dailyLoss: dataForm.value.drawdown.value,
      account: marginAllocation.value!.accountId
    });

    updateBalanceDrawdown();

    const text = isDeallocate ? 'desalocado' : 'salvo';

    notify({
      group: 'wrn-notifications',
      text: `Seu limite foi ${text} com sucesso`,
      type: NotificationTypes.success
    });
  } catch (e) {
    const text = isDeallocate ? 'desalocar' : 'salvar';
    let message = `Houve um problema ao ${text} seu limite. Tente novamente em breve.`;

    if (isBFFError(e)) {
      message = e.error.message.formatted;
    }

    notify({
      group: 'wrn-notifications',
      text: message,
      type: NotificationTypes.error
    });
  } finally {
    dataForm.value.isUpdatingLimits = false;
  }
}

async function fetchUpdatePreference() {
  const value = !dataForm.value.isPreferencesSaved;

  try {
    dataForm.value.isUpdatingPreferences = true;

    await updatePreferences(marginAllocation.value!.accountId);
    marginAllocation.value = await getMarginAllocationV2();
    updateBalanceDrawdown();

    const message = value
      ? 'Suas preferências foram salvas e serão mantidas assim até a próxima edição.'
      : 'As preferências foram desativadas.';

    notify({
      group: 'wrn-notifications',
      text: message,
      type: NotificationTypes.success
    });
  } catch {
    const message = value ? 'salvar' : 'desativar';

    notify({
      group: 'wrn-notifications',
      text: `Houve um problema ao ${message} suas preferências. Tente novamente em breve.`,
      type: NotificationTypes.error
    });
  } finally {
    dataForm.value.isUpdatingPreferences = false;
  }
}

onBeforeMount(() => {
  fetchInitialData();
});

provide('marginAllocation', marginAllocation);
provide('marginAllocationPreview', marginAllocationPreview);
provide('dataForm', dataForm);

provide('fetchInitialData', fetchInitialData);
provide('updateDataForm', updateDataForm);
provide('fetchMarginAllocation', fetchMarginAllocation);
provide('fetchUpdatePreference', fetchUpdatePreference);
provide('fetchUpdateLimits', fetchUpdateLimits);
provide('changeStep', changeStep);
</script>

<style lang="less" scoped>
@navbar-height: 64px;

.margin-allocation {
  background-color: @background-tertiary;
  min-height: 100vh;

  &--secondary {
    background-color: @background-secondary;
  }

  .navbar-container {
    background-color: transparent;
    border-bottom: none;

    &--secondary {
      background-color: @background-secondary;
    }
  }

  &__loader {
    height: calc(100vh - @navbar-height);
  }
}
</style>
